import { lazy, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom'

import AuthProvider from './auth/AuthProvider'
import RequireAuth from './auth/RequireAuth'
import { useAuth } from './auth/AuthContext'
import AppLayout from './app/AppLayout'
import LegacyHashRedirect from './app/LegacyHashRedirect'
import { ROUTES } from './app/routes'
import { FormRecoveryNavigator } from './lib/useFormRecovery'

import Login from './pages/Login'
import Signup from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ConfirmEmail from './pages/ConfirmEmail'
import { RouteLoaderWindow } from './ui/GlobalLoader'

// Route-level code splitting: putting 24 screens in one bundle would make the
// first paint pay for every screen a user never opens.
const SCREEN_LOADERS = {
  dashboard: () => import('./screens/Dashboard'),
  inbox: () => import('./screens/Inbox'),
  matching: () => import('./screens/Matching'),
  jobs: () => import('./screens/Jobs'),
  candidates: () => import('./screens/Candidates'),
  cvbank: () => import('./screens/CvBank'),
  pipeline: () => import('./screens/Pipeline'),
  progress: () => import('./screens/Progress'),
  import: () => import('./screens/CvImport'),
  jobboard: () => import('./screens/JobBoard'),
  recruiterhub: () => import('./screens/RecruiterHub'),
  talent: () => import('./screens/Talent'),
  tasks: () => import('./screens/Tasks'),
  aiassistant: () => import('./screens/AiAssistant'),
  interviews: () => import('./screens/Interviews'),
  requisitions: () => import('./screens/Requisitions'),
  assessments: () => import('./screens/Assessments'),
  offers: () => import('./screens/Offers'),
  approvals: () => import('./screens/Approvals'),
  managers: () => import('./screens/Managers'),
  departments: () => import('./screens/Departments'),
  deptmanagers: () => import('./screens/DepartmentManagers'),
  calendar: () => import('./screens/Calendar'),
  reports: () => import('./screens/Reports'),
  analytics: () => import('./screens/Analytics'),
  aistudio: () => import('./screens/AiStudio'),
  notifications: () => import('./screens/Notifications'),
  activity: () => import('./screens/SystemAnalytics'),
  settings: () => import('./screens/Settings'),
  help: () => import('./screens/Help'),
  profile: () => import('./screens/MyProfile'),
}

const SCREENS = Object.fromEntries(
  Object.entries(SCREEN_LOADERS).map(([path, load]) => [path, lazy(load)]),
)

/**
 * Once the signed-in shell is idle, fetch the code of every screen this role
 * can open, one at a time. A sidebar click then renders from the module cache
 * instead of waiting on a chunk download — the "blank pause" on first visit to
 * each screen. Skipped on Save-Data / 2G, and it never competes with the first
 * paint: it starts after an idle callback and walks the list sequentially.
 *
 * Once per page load, on purpose. `can` changes identity on every session
 * refresh, and a re-run hours later — after a deploy — would request chunk
 * hashes the server no longer has; nginx answers those with a reload stub
 * (@stale_js), which would reload the page under the user. The first run
 * happens seconds after load, against the build this tab was served.
 */
let screenPrefetchStarted = false

function ScreenPrefetch() {
  const { user, can } = useAuth()
  useEffect(() => {
    if (!user || screenPrefetchStarted) return undefined
    const conn = navigator.connection
    if (conn?.saveData || /(^|-)2g$/.test(conn?.effectiveType || '')) return undefined
    const queue = ROUTES
      .filter((r) => !r.hidden && !r.comingSoon && (!r.permission || can(r.permission)))
      .map((r) => SCREEN_LOADERS[r.path])
      .filter(Boolean)
    // Not tied to this effect's lifetime: a dependency change must not abort
    // a run that will never restart. It only ever imports modules, so letting
    // it finish is harmless.
    screenPrefetchStarted = true
    const idle = window.requestIdleCallback ?? ((cb) => window.setTimeout(cb, 1500))
    const next = () => {
      const load = queue.shift()
      if (!load) return
      // A failed prefetch is harmless: the route loads (and reports) on click.
      load().catch(() => {}).finally(() => idle(next))
    }
    idle(next)
    return undefined
  }, [user, can])
  return null
}

// Detail pages live outside the ROUTES table (no sidebar entry, parameterized path).
const CandidatePage = lazy(() => import('./screens/CandidatePage'))
const ComingSoon = lazy(() => import('./screens/ComingSoon'))

function LegacyCandidateRedirect() {
  const { userId } = useParams()
  const [params] = useSearchParams()
  const tab = params.get('tab')
  const to = `/candidate/${encodeURIComponent(userId)}`
  return <Navigate to={tab ? `${to}?tab=${encodeURIComponent(tab)}` : to} replace />
}
const JobProfile = lazy(() => import('./screens/JobProfile'))
// Approval email / notification target. No permission gate: department heads
// may not hold offers.view; the server checks creator / approver / admin.
const OfferApproval = lazy(() => import('./screens/OfferApproval'))

/** Landing page: the first sidebar screen this role's permissions allow. */
function HomeRedirect() {
  const { can } = useAuth()
  const home = ROUTES.find((r) => !r.hidden && !r.comingSoon && can(r.permission))
  return <Navigate to={`/${home?.path ?? 'help'}`} replace />
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Opens a loading window for the global loader on each new screen. */}
        <RouteLoaderWindow />
        <LegacyHashRedirect />
        <ScreenPrefetch />
        <FormRecoveryNavigator />
        <Routes>
          {/* Public. These keep the /auth prefix as ROUTE paths so the backend's
              CONFIRM_EMAIL_PATH=/auth/confirm-email links resolve unchanged. */}
          <Route path="/auth" element={<Navigate to="/auth/login" replace />} />
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/signup" element={<Signup />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/confirm-email" element={<ConfirmEmail />} />

          {/* Protected */}
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            {ROUTES.map((r) => {
              const Screen = SCREENS[r.path]
              if (r.comingSoon) {
                return (
                  <Route
                    key={r.path}
                    path={`/${r.path}`}
                    element={<ComingSoon route={r}><Screen /></ComingSoon>}
                  />
                )
              }
              return (
                <Route
                  key={r.path}
                  path={`/${r.path}`}
                  element={
                    r.permission ? (
                      <RequireAuth permission={r.permission}>
                        <Screen />
                      </RequireAuth>
                    ) : (
                      <Screen />
                    )
                  }
                />
              )
            })}
            <Route
              path="/candidate/:userId"
              element={
                <RequireAuth permission="candidates.view">
                  <CandidatePage />
                </RequireAuth>
              }
            />
            <Route
              path="/job/:jobId"
              element={
                <RequireAuth permission="jobs.view">
                  <JobProfile />
                </RequireAuth>
              }
            />
            <Route path="/offer-approval/:placementId" element={<OfferApproval />} />
            <Route
              path="/candidates/:userId"
              element={
                <RequireAuth permission="candidates.view">
                  <LegacyCandidateRedirect />
                </RequireAuth>
              }
            />
            {/* Access Control moved into Settings; keep old /rbac bookmarks working. */}
            <Route path="/rbac" element={<Navigate to="/settings?tab=Access+Control" replace />} />
          </Route>

          <Route path="/" element={<RequireAuth><HomeRedirect /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
